import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { readDeepLinkAction, stripDeepLinkAction } from "Utils/deepLinks";

/**
 * Run a one-shot handler when the URL asks for an in-app action.
 *
 * Backs the `?action=<name>` deep links described in Utils/deepLinks.js. Several
 * SkillsBridge actions live in drawers or tabs rather than on routes of their
 * own, so a link to the parent page leaves the user hunting for the control.
 * Ask Allie's knowledge-base "Click here" links are the main consumer.
 *
 * The three things every consumer must get right, handled here once:
 *   1. Fire ONCE PER REQUEST. A page that re-renders (or re-runs the effect) must
 *      not reopen a drawer the user has since closed — but a NEW request for the
 *      same action must still fire. The latch is therefore released as soon as the
 *      param is gone: without that, a link clicked while already on the target
 *      page is a silent no-op for the rest of the page's lifetime (Ask Allie's
 *      "Click here" links hit this constantly, since the answer usually points at
 *      the page the user is already reading).
 *   2. Respect permissions. `allowed` should be the SAME entitlement check that
 *      gates the equivalent button — a deep link must never be a way around it.
 *   3. Strip the param (replace: true) so refresh and Back don't re-fire.
 *
 * @param {object}   opts
 * @param {string}   opts.action    action name to match (DEEP_LINK_ACTIONS.*)
 * @param {Function} opts.onAllowed run when the action is requested and permitted
 * @param {boolean}  opts.ready     false while prerequisites (e.g. entitlements)
 *                                  are still loading; the action waits rather
 *                                  than being evaluated against missing data
 * @param {boolean}  opts.allowed   whether the caller may perform the action
 * @param {Function} [opts.onDenied] run instead when requested but not permitted
 */
const useDeepLinkAction = ({ action, onAllowed, ready = true, allowed = true, onDenied }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef(false);

  // Keep the latest callbacks without making them effect dependencies — callers
  // routinely pass inline arrows, which would otherwise re-run this every render.
  const onAllowedRef = useRef(onAllowed);
  const onDeniedRef = useRef(onDenied);
  onAllowedRef.current = onAllowed;
  onDeniedRef.current = onDenied;

  useEffect(() => {
    const requested = readDeepLinkAction(location.search) === action;

    // Param gone (we stripped it, or the user navigated) — arm for the next
    // request. Runs before the `ready` bail-out so a not-yet-ready page still
    // re-arms.
    if (!requested) {
      handledRef.current = false;
      return;
    }

    if (handledRef.current || !ready) return;

    handledRef.current = true;
    navigate(
      { pathname: location.pathname, search: stripDeepLinkAction(location.search) },
      { replace: true }
    );

    if (allowed) onAllowedRef.current?.();
    else onDeniedRef.current?.();
  }, [action, ready, allowed, location.search, location.pathname, navigate]);
};

export default useDeepLinkAction;
